import React from 'react';
import type { Holiday } from '../services/AcademicYearService';

interface HolidayOverlayProps {
    holiday: Holiday | null;
    className?: string;
}

/**
 * Holiday overlay component that displays a visual indicator when a day is a holiday
 */
const HolidayOverlay: React.FC<HolidayOverlayProps> = ({ holiday, className = '' }) => {
    if (!holiday) {
        return null;
    }

    // Different styles based on holiday type
    const getHolidayStyle = (type: string) => {
        switch (type?.toLowerCase()) {
            case 'break':
            case 'school break':
                return {
                    backgroundColor: 'rgba(255, 193, 7, 0.02)', // Very light warm yellow
                    borderColor: 'rgba(255, 193, 7, 0.3)',
                    iconColor: '#F59E0B',
                };
            case 'holiday':
            case 'public holiday':
                return {
                    backgroundColor: 'rgba(239, 68, 68, 0.02)', // Very light red
                    borderColor: 'rgba(239, 68, 68, 0.25)',
                    iconColor: '#DC2626',
                };
            case 'vacation':
                return {
                    backgroundColor: 'rgba(34, 197, 94, 0.02)', // Very light green
                    borderColor: 'rgba(34, 197, 94, 0.25)',
                    iconColor: '#16A34A',
                };
            default:
                return {
                    backgroundColor: 'rgba(156, 163, 175, 0.02)', // Light gray
                    borderColor: 'rgba(156, 163, 175, 0.4)',
                    iconColor: '#6B7280',
                };
        }
    };

    const style = getHolidayStyle(holiday.type);

    return (
        <div
            className={`absolute inset-0 pointer-events-none z-30 flex items-center justify-center ${className}`}
            style={{
                backgroundColor: style.backgroundColor,
                borderRadius: '8px'
            }}
            title={`${holiday.name}${holiday.description ? ` - ${holiday.description}` : ''}`}
        >
            {/* Holiday indicator */}
            <div
                className="absolute top-2 right-2 text-2xl opacity-80"
                style={{ color: style.iconColor }}
            >
            </div>

            {/* Holiday name (shown on larger overlays) */}
            <div className="text-center px-2 py-1 max-w-full">
                <div
                    className="text-sm font-semibold opacity-90 truncate"
                    style={{ color: style.iconColor }}
                >
                    {holiday.name}
                </div>
                {holiday.type && (
                    <div
                        className="text-xs opacity-70 capitalize truncate"
                        style={{ color: style.iconColor }}
                    >
                        {holiday.type}
                    </div>
                )}
            </div>

            {/* Diagonal stripes pattern for all holiday types */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 60px,
                        ${style.borderColor} 60px,
                        ${style.borderColor} 120px
                    )`,
                    opacity: 0.15,
                    borderRadius: '6px'
                }}
            />
        </div>
    );
};

export default HolidayOverlay; 